import React, { useEffect, useId, useState } from 'react';
import { useAuth } from '../auth/AuthContext.jsx';
import { getSsoSelfServiceUrl, SSO_WARNING_DISMISSED_KEY } from '../config/ssoWarning.js';
import { logger } from '../lib/logger.js';
import '../styles/components/sso-deprecation-banner.css';

/**
 * Email/password sign-in deprecation notice (sso-warning).
 *
 * variant='full'    — AppLayout banner: headline, link, "How to switch"
 *                     instructions, per-session dismiss. Shown only for an
 *                     authenticated email/password session.
 * variant='compact' — headline + link only, no dismiss, no auth gating
 *                     (for the pre-sign-in login page).
 *
 * Always on in every environment; the self-service URL comes from
 * VITE_SSO_SELFSERVICE_URL via config/ssoWarning.js.
 */

const DISMISSED_KEY = SSO_WARNING_DISMISSED_KEY;
const HEADLINE = 'Email and password sign-in is being retired - switch to BanyanSSO';

function readDismissed() {
  try {
    return sessionStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

function writeDismissed() {
  try {
    sessionStorage.setItem(DISMISSED_KEY, '1');
  } catch {
    /* private mode / blocked storage: in-memory state still hides the panel */
  }
}

export default function SsoDeprecationBanner({ variant = 'full' }) {
  const { isAuthenticated, isLoading, isPasswordSession } = useAuth();
  const [dismissed, setDismissed] = useState(readDismissed);
  const headingId = useId();

  const compact = variant === 'compact';
  const visible = compact
    ? true
    : isAuthenticated && !isLoading && isPasswordSession && !dismissed;
  const url = getSsoSelfServiceUrl();

  useEffect(() => {
    if (visible && !url) {
      logger.warn('sso_warning.selfservice_url_missing', {
        variable: 'VITE_SSO_SELFSERVICE_URL',
      });
    }
  }, [visible, url]);

  if (!visible) return null;

  function handleDismiss() {
    writeDismissed();
    setDismissed(true);
  }

  const link = url ? (
    <a
      className="sso-banner__link"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
    >
      Go to the BanyanSSO self-service site
    </a>
  ) : null;

  return (
    <section
      className={`sso-banner sso-banner--${compact ? 'compact' : 'full'}`}
      role="region"
      aria-labelledby={headingId}
    >
      <div className="sso-banner__body">
        <p id={headingId} className="sso-banner__headline">
          {HEADLINE}
        </p>
        {link}
        {!compact && (
          <details className="sso-banner__details">
            <summary>How to switch</summary>
            <ol className="sso-banner__steps">
              <li>
                Go to the self-service site and enter your work email. Every
                registered company domain is recognized and routed to the right
                place.
              </li>
              <li>
                Your CEO signs in with a one-time code (no password) and
                nominates a technical contact - about 2 minutes.
              </li>
              <li>
                The technical contact follows a guided setup to connect your
                company&apos;s existing login system, such as Microsoft 365 or
                Google Workspace - about 15 minutes. Staff keep the accounts
                they already use.
              </li>
              <li>
                Companies without an internal SSO can use username/password
                accounts with two-factor authentication instead.
              </li>
            </ol>
          </details>
        )}
      </div>
      {!compact && (
        <button type="button" className="sso-banner__dismiss" onClick={handleDismiss}>
          Dismiss for this session
        </button>
      )}
    </section>
  );
}
